import { execFile } from "child_process";
import { platform } from "os";
import { basename, dirname } from "path";
import { isWindows } from "./platform";
import {
  CLAUDE_CODE_PROVIDER,
  CODEX_CLI_PROVIDER,
  CURSOR_PROVIDER,
  type ProviderName,
} from "./providers";
import type { DiscoveredProcess } from "./types";

export async function discoverClaudeProcesses(): Promise<DiscoveredProcess[]> {
  if (platform() === "win32") return discoverWindows();
  return discoverUnix();
}

// ─── Command-line identification ───────────────────────────────────

// The npm-installed CLI runs as `node <...>/claude-code/cli.js`, so matching on
// the process NAME alone (the old `pgrep -x claude` / `IMAGENAME eq claude.exe`)
// misses every shim install — the process is called `node`. Identify by what is
// actually being executed instead.
const CLAUDE_CLI_SCRIPT = /claude-code[\\/](?:cli|index)\.(?:js|mjs|cjs)$/i;
const JS_RUNTIMES = new Set(["node", "node.exe", "bun", "bun.exe", "deno", "deno.exe"]);

// Split a command line into tokens, honouring double-quoted paths (Windows
// installs live under "C:\Program Files\..." and would otherwise split apart).
export function tokenizeCommandLine(commandLine: string): string[] {
  const tokens: string[] = [];
  let current = "";
  let quoted = false;
  for (const ch of commandLine) {
    if (ch === '"') {
      quoted = !quoted;
      continue;
    }
    if (!quoted && (ch === " " || ch === "\t")) {
      if (current) tokens.push(current);
      current = "";
      continue;
    }
    current += ch;
  }
  if (current) tokens.push(current);
  return tokens;
}

// A command line is just text and may carry a path in either separator form,
// so `path.basename` cannot be used here — it honours only the host's
// separator and would leave a Windows path intact when parsed on POSIX. The
// script-path regex above is separator-agnostic for the same reason.
function exeBaseName(token: string): string {
  const cut = Math.max(token.lastIndexOf("/"), token.lastIndexOf("\\"));
  return cut === -1 ? token : token.slice(cut + 1);
}

/**
 * Whether a command line is a Claude Code CLI process.
 *
 * Accepts a native binary invoked by any path, and a JS runtime hosting the
 * published CLI entry point. Deliberately conservative: it inspects only the
 * executable and the script it runs, never the whole string, so an unrelated
 * process that merely mentions "claude" in a flag (an editor session, this
 * streamer itself) is not mistaken for an agent.
 */
export function looksLikeClaudeProcess(commandLine: string): boolean {
  const tokens = tokenizeCommandLine(commandLine);
  if (tokens.length === 0) return false;

  const exe = exeBaseName(tokens[0]).toLowerCase();
  if (exe === "claude" || exe === "claude.exe") return true;

  if (JS_RUNTIMES.has(exe)) {
    // The script is the first non-flag argument (`node --flag script.js ...`).
    for (const raw of tokens.slice(1)) {
      if (raw.startsWith("-")) continue;
      return CLAUDE_CLI_SCRIPT.test(raw);
    }
  }
  return false;
}

/**
 * Codex subcommands that are never an interactive session.
 *
 * A deny-list, arrived at the hard way. The obvious allow-list — "the
 * subcommand must be `resume` or absent" — cannot be written correctly, because
 * finding "the subcommand" means knowing which flags take a value: `codex --cd
 * /srv/app` would read the path as the subcommand and reject a real session,
 * and there is no reliable enumeration of Codex's value-taking flags to fix
 * that with.
 *
 * Over-accepting is the risk a deny-list carries, and here it is bounded. A
 * process only becomes adoptable if it also states a rollout id, which is what
 * `codex resume <uuid>` does and what no non-interactive subcommand does — so a
 * future non-interactive verb missing from this list is discovered but never
 * reaches adopt, because SessionStore drops a discovered process that names no
 * conversation.
 */
const CODEX_NON_INTERACTIVE_SUBCOMMANDS = new Set([
  "app-server",
  "exec",
  "mcp",
  "proto",
  "login",
  "logout",
  "completion",
]);

/**
 * Whether a command line is an interactive Codex CLI session.
 *
 * Far more selective than the Claude matcher has to be, because "codex" is a
 * crowded name on a machine that has the ChatGPT desktop app: its Electron
 * framework is literally `Codex Framework.framework`, its helpers are
 * `Codex (Renderer)` and `Codex (Service)`, and `~/.codex/` holds a Chrome
 * extension host and a computer-use service. Measured on one developer machine,
 * 21 processes matched the word "codex" and only 3 were sessions.
 *
 * Two discriminators do the work:
 *
 *  - The executable's base name must be exactly `codex`, which drops every
 *    Electron helper and the `codex-code-mode-host` sidecar (which does have a
 *    TTY, so liveness alone would not have separated it).
 *  - No token may be a non-interactive subcommand. This is the load-bearing
 *    one: the ChatGPT app's `/Applications/ChatGPT.app/Contents/Resources/codex`
 *    and the VS Code extension's bundled binary are both named exactly `codex`,
 *    and only `app-server` distinguishes them from a session someone is typing
 *    at. Scanning every token rather than locating the subcommand means a
 *    value that happens to equal one of these names costs a missed session,
 *    never a wrongly claimed one.
 */
export function looksLikeCodexProcess(commandLine: string): boolean {
  const tokens = tokenizeCommandLine(commandLine);
  if (tokens.length === 0) return false;

  const exe = exeBaseName(tokens[0]).toLowerCase();
  if (exe !== "codex" && exe !== "codex.exe") return false;

  // A macOS bundle path contains spaces, so tokenizing splits it mid-path and
  // the first token can END at a directory called "Codex" — which is exactly
  // what `.../Frameworks/Codex Framework.framework/.../Codex (Renderer)` and
  // `~/.codex/computer-use/Codex Computer Use.app/...` do. Their base name then
  // reads as `codex` and the executable check alone lets them through. No real
  // CLI invocation has a bundle marker anywhere in it.
  if (tokens.some((token) => /\.(?:app|framework)[\\/]/i.test(token))) return false;

  for (const token of tokens.slice(1)) {
    if (CODEX_NON_INTERACTIVE_SUBCOMMANDS.has(token.toLowerCase())) return false;
  }
  return true;
}

const CURSOR_NON_INTERACTIVE_SUBCOMMANDS = new Set([
  "login",
  "logout",
  "mcp",
  "sandbox",
  "worker",
  "acp",
  "update",
  "create-chat",
  "generate-rule",
  "rule",
  "help",
  "about",
  "status",
  "whoami",
  "models",
  "install-shell-integration",
  "uninstall-shell-integration",
]);

export function looksLikeCursorProcess(commandLine: string): boolean {
  const tokens = tokenizeCommandLine(commandLine);
  if (tokens.length === 0) return false;

  const exe = exeBaseName(tokens[0]).toLowerCase();
  if (
    exe !== "agent" &&
    exe !== "agent.exe" &&
    exe !== "cursor-agent" &&
    exe !== "cursor-agent.exe"
  ) {
    return false;
  }

  for (const token of tokens.slice(1)) {
    if (CURSOR_NON_INTERACTIVE_SUBCOMMANDS.has(token.toLowerCase())) return false;
  }
  return true;
}

/** Which agent a command line is, or null when it is neither. */
export function providerForCommandLine(commandLine: string): ProviderName | null {
  if (looksLikeClaudeProcess(commandLine)) return CLAUDE_CODE_PROVIDER;
  if (looksLikeCodexProcess(commandLine)) return CODEX_CLI_PROVIDER;
  if (looksLikeCursorProcess(commandLine)) return CURSOR_PROVIDER;
  return null;
}

// Codex resumes by positional rollout id (`codex resume <uuid>`), not by the
// `--resume <id>` flag Claude uses, so the Claude extractor cannot see it.
// Anchored on a UUID shape rather than "the token after resume": `codex resume
// --last` picks the most recent session and carries no id at all, and adopting
// a session under the id "--last" would name a conversation that does not exist.
const CODEX_ROLLOUT_ID =
  /\bresume\s+([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\b/i;

export function extractCodexResumeId(args: string): string | null {
  return args.match(CODEX_ROLLOUT_ID)?.[1] ?? null;
}

/** The conversation id a discovered process is working on, if it states one. */
export function extractConversationId(args: string, provider: ProviderName): string | null {
  return provider === CODEX_CLI_PROVIDER ? extractCodexResumeId(args) : extractResumeId(args);
}

async function discoverUnix(): Promise<DiscoveredProcess[]> {
  const pids = await getPidsUnix();

  const results = await Promise.all(
    pids.map(async (pid) => {
      try {
        const [cwd, args, startedAt] = await Promise.all([
          getProcessCwdUnix(pid),
          getProcessArgsUnix(pid),
          getProcessStartTimeUnix(pid),
        ]);
        // Re-derived here rather than carried from the sweep: the pgrep
        // fallback matches on name alone, so this is the only point at which
        // `codex app-server` can be told apart from a session.
        const provider = providerForCommandLine(args);
        if (provider === null) return null;

        return {
          pid,
          provider,
          projectPath: cwd,
          projectName: basename(cwd),
          branch: await readGitBranch(cwd),
          conversationId: extractConversationId(args, provider),
          startedAt,
        } satisfies DiscoveredProcess;
      } catch {
        return null;
      }
    }),
  );

  return results.filter((r): r is DiscoveredProcess => r !== null);
}

async function discoverWindows(): Promise<DiscoveredProcess[]> {
  // Preferred: one CIM query for every candidate process. This finds npm-shim
  // installs (which run as node.exe) and drops the wmic dependency — wmic is
  // removed on current Windows 11 builds, where the legacy path below silently
  // yields nothing. Falls back to tasklist+wmic if PowerShell/CIM is unavailable.
  const viaCim = await discoverWindowsViaCim();
  if (viaCim) return viaCim;

  const pids = await getPidsWindows();

  const results = await Promise.all(
    pids.map(async (pid) => {
      try {
        const info = await getProcessInfoWindows(pid);
        if (!info) return null;

        const provider = providerForCommandLine(info.args);
        if (provider === null) return null;

        return {
          pid,
          provider,
          projectPath: info.cwd,
          projectName: basename(info.cwd),
          branch: await readGitBranch(info.cwd),
          conversationId: extractConversationId(info.args, provider),
          startedAt: info.startedAt,
        } satisfies DiscoveredProcess;
      } catch {
        return null;
      }
    }),
  );

  return results.filter((r): r is DiscoveredProcess => r !== null);
}

// ─── Unix Helpers ──────────────────────────────────────────────────

function run(
  cmd: string,
  args: string[],
  opts: { cwd?: string; timeout?: number } = {},
): Promise<string> {
  return new Promise((resolve, reject) => {
    execFile(
      cmd,
      args,
      { windowsHide: isWindows, encoding: "utf-8", timeout: opts.timeout ?? 5000, cwd: opts.cwd },
      (err, stdout) => {
        if (err) reject(err);
        else resolve(stdout as string);
      },
    );
  });
}

// Parse `ps -eo pid=,args=` output into pids whose command line is an agent CLI.
// Exported for tests — this is where shim installs (running as `node`) are
// recovered, which a name-only `pgrep -x claude` can never see.
export function parsePsOutput(stdout: string): number[] {
  const pids: number[] = [];
  for (const line of stdout.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const match = trimmed.match(/^(\d+)\s+(.*)$/);
    if (!match) continue;
    const pid = Number.parseInt(match[1], 10);
    if (!(pid > 0)) continue;
    if (providerForCommandLine(match[2]) !== null) pids.push(pid);
  }
  return pids;
}

async function getPidsUnix(): Promise<number[]> {
  // One `ps` sweep matched on the command line. Falls back to the historical
  // name-only pgrep if ps is unavailable; that path finds native-binary installs
  // only, which is still better than returning nothing.
  try {
    return parsePsOutput(await run("ps", ["-eo", "pid=,args="]));
  } catch {
    // fall through
  }
  // Name-only fallback, one sweep per agent. It cannot see shim installs (those
  // run as `node`) and cannot tell an interactive `codex` from the ChatGPT
  // app's `codex app-server`, so the per-pid args re-read in discoverUnix is
  // what actually filters this path — it drops anything that is neither agent.
  const pids: number[] = [];
  for (const name of ["claude", "codex"]) {
    try {
      const output = await run("pgrep", ["-x", name]);
      for (const line of output.trim().split("\n")) {
        const pid = Number.parseInt(line, 10);
        if (pid > 0) pids.push(pid);
      }
    } catch {
      // No match for this agent, or no pgrep at all. Try the next one.
    }
  }
  return pids;
}

async function getProcessCwdUnix(pid: number): Promise<string> {
  const output = await run("lsof", ["-p", String(pid), "-a", "-d", "cwd", "-Fn"]);
  const match = output.match(/n(.+)/);
  return match?.[1] ?? "";
}

async function getProcessArgsUnix(pid: number): Promise<string> {
  return (await run("ps", ["-p", String(pid), "-o", "args="])).trim();
}

/**
 * Full command line of a single live process, or "" if it cannot be read.
 *
 * Used by the boot reconciler's pid-reuse guard: a recorded pid being alive is
 * never sufficient evidence that the process is still ours, because pids get
 * recycled. The caller matches its stored token against this before claiming a
 * surviving session — and treats an unreadable command line as "cannot confirm"
 * rather than "confirmed", so a failure here can never promote an unrelated
 * process into a managed session.
 */
export async function getProcessArgs(pid: number): Promise<string> {
  if (!Number.isInteger(pid) || pid < 1) return "";
  try {
    if (platform() === "win32") {
      const info = await getProcessInfoWindows(pid);
      return info?.args ?? "";
    }
    return await getProcessArgsUnix(pid);
  } catch {
    // Process vanished between the liveness probe and this call, or ps/wmic is
    // unavailable. Either way we cannot confirm identity.
    return "";
  }
}

async function getProcessStartTimeUnix(pid: number): Promise<Date> {
  const raw = (await run("ps", ["-p", String(pid), "-o", "lstart="])).trim();
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

// ─── Windows Helpers ───────────────────────────────────────────────

interface CimProcess {
  ProcessId: number;
  CommandLine: string | null;
  CreationDate: string | null;
}

// Parse the CIM/PowerShell JSON payload. Exported for tests: the shape differs
// for a single result (bare object) vs several (array), and CreationDate can
// arrive as an ISO string or as the /Date(ms)/ serialization.
export function parseCimProcesses(stdout: string): CimProcess[] {
  const trimmed = stdout.trim();
  if (!trimmed) return [];
  const parsed = JSON.parse(trimmed);
  return Array.isArray(parsed) ? parsed : [parsed];
}

export function parseCimDate(value: string | null): Date {
  if (value) {
    const epoch = value.match(/\/Date\((\d+)\)\//);
    if (epoch) return new Date(Number(epoch[1]));
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date();
}

async function discoverWindowsViaCim(): Promise<DiscoveredProcess[] | null> {
  let stdout: string;
  try {
    // WQL filter at the provider — dumping every Win32_Process as JSON is what
    // made GET /api/sessions take tens of seconds on a busy Windows box. Claude
    // still runs as claude.exe or under a JS runtime (npm shim); looksLikeClaudeProcess
    // below drops unrelated node/bun/deno processes.
    stdout = await run("powershell.exe", [
      "-NoProfile",
      "-NonInteractive",
      "-Command",
      "Get-CimInstance Win32_Process -Filter \"Name = 'claude.exe' OR Name = 'claude' OR Name = 'codex.exe' OR Name = 'codex' OR Name = 'node.exe' OR Name = 'bun.exe' OR Name = 'deno.exe'\" | Select-Object ProcessId,CommandLine,CreationDate | ConvertTo-Json -Compress",
    ]);
  } catch {
    return null; // PowerShell unavailable — let the caller fall back.
  }

  let rows: CimProcess[];
  try {
    rows = parseCimProcesses(stdout);
  } catch {
    return null;
  }

  const results: DiscoveredProcess[] = [];
  for (const row of rows) {
    const commandLine = row.CommandLine ?? "";
    if (!commandLine) continue;
    // The CIM filter above matches on process name, which cannot separate an
    // interactive `codex` from the ChatGPT app's `codex app-server`. This does.
    const provider = providerForCommandLine(commandLine);
    if (provider === null) continue;
    // Windows exposes no process CWD (neither CIM nor wmic carries it), so the
    // project path is genuinely unknown here rather than guessed. The previous
    // code substituted the executable's own directory, which reported an
    // unrelated install path as the user's project.
    results.push({
      pid: row.ProcessId,
      provider,
      projectPath: "",
      projectName: "",
      branch: "",
      conversationId: extractConversationId(commandLine, provider),
      startedAt: parseCimDate(row.CreationDate),
    });
  }
  return results;
}

async function getPidsWindows(): Promise<number[]> {
  try {
    const output = await run("tasklist", ["/FI", "IMAGENAME eq claude.exe", "/FO", "CSV", "/NH"]);
    return output
      .trim()
      .split("\n")
      .filter(Boolean)
      .map((line) => {
        const parts = line.split(",");
        return Number.parseInt(parts[1]?.replace(/"/g, "") ?? "0", 10);
      })
      .filter((pid) => pid > 0);
  } catch {
    return [];
  }
}

async function getProcessInfoWindows(
  pid: number,
): Promise<{ cwd: string; args: string; startedAt: Date } | null> {
  try {
    const output = await run("wmic", [
      "process",
      "where",
      `ProcessId=${pid}`,
      "get",
      "CommandLine,CreationDate,ExecutablePath",
      "/FORMAT:CSV",
    ]);
    // wmic uses CRLF; split on \r?\n so the blank separator line becomes "" and is filtered out.
    const lines = output
      .trim()
      .split(/\r?\n/)
      .filter((l) => l.trim().length > 0);
    if (lines.length < 2) return null;

    const parts = lines[1].split(",");
    const args = parts[1] ?? "";
    const creationDate = parts[2] ?? "";

    // WMIC CreationDate format: 20260418153000.000000+000
    const year = creationDate.slice(0, 4);
    const month = creationDate.slice(4, 6);
    const day = creationDate.slice(6, 8);
    const hour = creationDate.slice(8, 10);
    const min = creationDate.slice(10, 12);
    const sec = creationDate.slice(12, 14);
    const startedAt = new Date(`${year}-${month}-${day}T${hour}:${min}:${sec}`);
    if (Number.isNaN(startedAt.getTime())) return null;

    // CWD is not directly available via wmic; use the executable path's parent directory as fallback
    const exePath = parts[3] ?? "";
    const cwd = exePath ? dirname(exePath) : "";

    return { cwd, args, startedAt };
  } catch {
    return null;
  }
}

// ─── Shared Helpers ────────────────────────────────────────────────

// Pull the conversation id out of a command line. Handles the forms Claude Code
// accepts — `--resume <id>`, `--resume=<id>`, and the `-r` short flag — and
// refuses a value that is itself a flag: `claude --resume --model opus` has no
// id, and capturing "--model" would surface a session under a garbage id.
export function extractResumeId(args: string): string | null {
  const eq = args.match(/(?:--resume|-r)=(\S+)/);
  if (eq?.[1] && !eq[1].startsWith("-")) return eq[1];
  const spaced = args.match(/(?:--resume|-r)\s+(\S+)/);
  const candidate = spaced?.[1];
  if (!candidate || candidate.startsWith("-")) return null;
  return candidate;
}

async function readGitBranch(dir: string): Promise<string> {
  // An empty cwd would make execFile inherit OUR working directory, so an
  // unreadable process reported the streamer's own branch as if it were its own
  // (observed live). No directory, no answer.
  if (!dir) return "";
  try {
    return (
      await run("git", ["rev-parse", "--abbrev-ref", "HEAD"], { cwd: dir, timeout: 3000 })
    ).trim();
  } catch {
    return "";
  }
}
